import axios from 'axios';
import * as Sentry from '@sentry/react';
import type {
  ComboPpeChecklistItem,
  ComboWeatherSnapshot,
  IntensityLabel,
  OperatorSpraySessionCombo,
  ProductConsumed,
  SpraySession,
  SprayProgramRef,
} from './sprayService';

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Shape of the populated parent session on combo rows returned by
 * GET /api/v1/spray/operator-sessions/my. The route does
 *   .populate({ path: 'sessionId', select: 'date orchardIds programId roundId status' })
 * so each combo's `sessionId` field is an object with these keys instead of
 * the raw ObjectId string.
 *
 * `programId` is itself populated by the same route so callers can render
 * the program name without an extra round trip.
 */
export interface PopulatedParentSession {
  _id: string;
  date: string;
  orchardIds: string[];
  programId: string | SprayProgramRef;
  roundId?: string;
  status?: string;
  /** Session display name when present (Phase 6 identity line). */
  name?: string;
  /** Tenant-scoped display sequence (SES-{seq}). */
  seq?: string | number;
  /** Categorical intensity label resolved against the machine calibration. */
  intensityLevel?: IntensityLabel;
}

/**
 * A row of the operator's "My Sessions" list: a per-combo record (one per
 * (operator × machine) assignment) plus the populated parent session it
 * belongs to. Inherits the combo shape from sprayService — adds nothing
 * beyond narrowing `sessionId` from `string` to the populated object.
 */
export type MyOperatorSessionRow = Omit<OperatorSpraySessionCombo, 'sessionId'> & {
  sessionId: PopulatedParentSession;
};

export interface MyOperatorSessionsResponse {
  sessions: MyOperatorSessionRow[];
  hasLinkedOperator: boolean;
}

/**
 * Shape returned by GET /api/v1/spray/operator-sessions/:id/mix-preview
 * (Task 6.6). Computed live from the parent session's intensity / orchard
 * area + the machine's current calibration. Powers the per-combo
 * `<MixAmountsModal>` (Task 6.15) and the live derived-products display in
 * the per-combo Complete / End-early forms (Task 6.14).
 */
export interface MixPreview {
  machine: {
    _id: string;
    name: string;
    tankVolume: number;
  };
  intensity: string;
  totalWaterL: number;
  tankCount: number;
  perTankMix: Array<{
    productId: string;
    productName: string;
    dosePerTank: number;
    doseUnit: string;
  }>;
}

/**
 * Operator-scoped data layer for the per-combo "My Sessions" flow
 * introduced in Phase 6 of the multi-operator spray sessions track.
 *
 * The earlier session-level operator list (sprayService.getMySessions) ran
 * against `/api/v1/spray/sessions/my` and returned parent SpraySession
 * documents where the caller was named in `operatorIds`. From Phase 6 onward
 * the operator UI works one assignment at a time, so this service hits the
 * per-combo route instead and returns `OperatorSpraySession` rows.
 *
 * Gating: the backend route is wrapped by `requiresLinkedOperator`, and the
 * frontend route that mounts the list is wrapped by `<RequireLinkedOperator>`
 * — a pure-manager caller never reaches this service.
 */
export const operatorSpraySessionService = {
  /**
   * Field Mode "My Sessions" — fetches the caller's actionable combos
   * (status ∈ {not_started, in_progress}) scoped to their linked
   * SprayOperator. Each row's `sessionId` is populated with the parent
   * session's date/orchard/program/round/status so the list can render
   * without a follow-up fetch.
   *
   *   GET /api/v1/spray/operator-sessions/my
   *
   * The response still carries `hasLinkedOperator: boolean` for backward
   * compatibility with the legacy session-level call site. Under the new
   * gate this is always `true` for callers who get through the route
   * guard; FarmAdmin bypass returns `{ sessions: [], hasLinkedOperator: false }`.
   */
  async getMyOperatorSessions(): Promise<MyOperatorSessionsResponse> {
    try {
      const res = await axios.get('/api/v1/spray/operator-sessions/my', {
        headers: authHeaders(),
      });
      return res.data;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  /**
   * Read a single combo. Backed by GET /api/v1/spray/operator-sessions/:id —
   * gated `requiresOperatorOrManager`. Operators can only read their own
   * combos (the route 403s otherwise); managers + Farm Admin read freely.
   *
   * Returns the combo with `sessionId` deep-populated (programId {_id,name}
   * + orchardIds + name/seq/intensityLevel), matching the /operator-sessions/my
   * shape. See #300: a QR-scan cold-cache load relies on this populate so the
   * operator workflow can render Program + Orchard without a second fetch.
   */
  async getCombo(comboId: string): Promise<MyOperatorSessionRow> {
    try {
      const res = await axios.get(`/api/v1/spray/operator-sessions/${comboId}`, {
        headers: authHeaders(),
      });
      return res.data;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  /**
   * Per-combo "start" lifecycle action. POSTs the PPE attestation array
   * server-side; the backend stamps `checked=true, checkedAt=now` on the
   * matching combo.ppeChecklist items and rejects with TransitionError if
   * any item is missing or unchecked.
   *
   *   POST /api/v1/spray/operator-sessions/:id/start
   *
   * Gated by `requiresLinkedOperator` on the server. The route additionally
   * enforces operator-self (the caller's linked SprayOperator._id must match
   * `combo.operatorId`) — Farm Admin bypasses that check.
   *
   * `ppeChecks` is optional for legacy combos with an empty `ppeChecklist`;
   * supply it on every modern combo. Task 6.4 widened the payload to accept
   * an optional `startedAt` override (operator-edited Start time) and the
   * `weatherStart` snapshot captured by the new combo workflow form.
   *
   * Phase 6 / Task 6.2 + Task 6.4.
   */
  async startCombo(
    comboId: string,
    payload: {
      ppeChecks?: Array<Pick<ComboPpeChecklistItem, 'item' | 'checked'>>;
      startedAt?: string;
      weatherStart?: ComboWeatherSnapshot;
    } = {},
  ): Promise<{ combo: OperatorSpraySessionCombo; parent: SpraySession }> {
    try {
      const res = await axios.post(
        `/api/v1/spray/operator-sessions/${comboId}/start`,
        payload,
        { headers: authHeaders() },
      );
      return res.data;
    } catch (err: any) {
      const backendMessage = err?.response?.data?.error;
      Sentry.captureException(err);
      if (backendMessage) {
        const wrapped = new Error(backendMessage);
        (wrapped as any).cause = err;
        throw wrapped;
      }
      throw err;
    }
  },

  /**
   * Per-combo "complete" lifecycle action. POSTs the consumed products,
   * tank count, and end-of-spray weather snapshot. The backend transitions
   * the combo to `completed` and recomputes the parent session's rollup
   * status.
   *
   *   POST /api/v1/spray/operator-sessions/:id/complete
   *
   * Gated by `requiresLinkedOperator` + operator-self (mirrors /start).
   *
   * Task 6.5 widened the payload to accept an optional `endTime` override
   * — the operator combo workflow form lets the operator edit the End time
   * before submitting, and the backend stamps the override onto the audit
   * trail. Task 6.7 made `productsConsumed` server-authoritative, so the
   * field is now optional on the wire (omitted = server derives from the
   * mix-preview snapshot).
   *
   * Phase 6 / Task 6.2 + Task 6.5 + Task 6.7.
   */
  async completeCombo(
    comboId: string,
    payload: {
      productsConsumed?: Array<ProductConsumed & { unit?: string }>;
      tankCount: number;
      weatherEnd: ComboWeatherSnapshot;
      endTime?: string;
    },
  ): Promise<{ combo: OperatorSpraySessionCombo; parent: SpraySession }> {
    try {
      const res = await axios.post(
        `/api/v1/spray/operator-sessions/${comboId}/complete`,
        payload,
        { headers: authHeaders() },
      );
      return res.data;
    } catch (err: any) {
      const backendMessage = err?.response?.data?.error;
      Sentry.captureException(err);
      if (backendMessage) {
        const wrapped = new Error(backendMessage);
        (wrapped as any).cause = err;
        throw wrapped;
      }
      throw err;
    }
  },

  /**
   * Per-combo "end early" lifecycle action — records the partial work that
   * happened on this assignment before the operator stopped. The backend
   * transitions the combo to `incomplete` and recomputes the parent
   * session's rollup status.
   *
   *   POST /api/v1/spray/operator-sessions/:id/end-early
   *
   * Gated by `requiresLinkedOperator` + operator-self (mirrors /start +
   * /complete). Task 6.10 hoisted the end-early/abandon calls from
   * `sprayService` onto this operator-scoped service so the new combo
   * workflow + sync drainer can both target a single source of truth.
   *
   * `productsConsumed` is optional per Task 6.7's server-authoritative
   * mix; `endTime` is the operator-edited End time override per Task 6.5.
   *
   * Phase 6 / Task 6.5 + Task 6.7 + Task 6.10.
   */
  async endEarlyCombo(
    comboId: string,
    payload: {
      productsConsumed?: Array<ProductConsumed & { unit?: string }>;
      tankCount: number;
      weatherEnd: ComboWeatherSnapshot;
      reason: string;
      endTime?: string;
    },
  ): Promise<{ combo: OperatorSpraySessionCombo; parent: SpraySession }> {
    try {
      const res = await axios.post(
        `/api/v1/spray/operator-sessions/${comboId}/end-early`,
        payload,
        { headers: authHeaders() },
      );
      return res.data;
    } catch (err: any) {
      const backendMessage = err?.response?.data?.error;
      Sentry.captureException(err);
      if (backendMessage) {
        const wrapped = new Error(backendMessage);
        (wrapped as any).cause = err;
        throw wrapped;
      }
      throw err;
    }
  },

  /**
   * Per-combo "abandon" lifecycle action — for the case where an operator
   * started the combo but no product was actually sprayed. Backend requires
   * the explicit `confirmedNoProductSprayed: true` flag so the audit trail
   * captures the operator's confirmation. The combo transitions to
   * `cancelled`.
   *
   *   POST /api/v1/spray/operator-sessions/:id/abandon
   *
   * Gated by `requiresLinkedOperator` + operator-self. Task 6.10 hoisted
   * this call onto the operator service alongside `endEarlyCombo`.
   *
   * Phase 6 / Task 6.10.
   */
  async abandonCombo(
    comboId: string,
    payload: {
      reason: string;
      confirmedNoProductSprayed: boolean;
    },
  ): Promise<{ combo: OperatorSpraySessionCombo; parent: SpraySession }> {
    try {
      const res = await axios.post(
        `/api/v1/spray/operator-sessions/${comboId}/abandon`,
        payload,
        { headers: authHeaders() },
      );
      return res.data;
    } catch (err: any) {
      const backendMessage = err?.response?.data?.error;
      Sentry.captureException(err);
      if (backendMessage) {
        const wrapped = new Error(backendMessage);
        (wrapped as any).cause = err;
        throw wrapped;
      }
      throw err;
    }
  },

  /**
   * Live mix preview for a single combo. Returns the machine + intensity +
   * water-volume + tank-count + per-tank dose rows the operator UI uses to:
   *   - render the live "derived products" block on Complete / End-early
   *     forms (Task 6.14 — multiply each dosePerTank by the operator's
   *     tankCount input).
   *   - power the `<MixAmountsModal>` summary (Task 6.15).
   *
   *   GET /api/v1/spray/operator-sessions/:id/mix-preview
   *
   * Gated `requiresOperatorOrManager` on the server. Computed without
   * persisting a SprayInstruction.
   *
   * Phase 6 / Task 6.6 (route) + Task 6.14 (consumer).
   */
  async getMixPreview(comboId: string): Promise<MixPreview> {
    try {
      const res = await axios.get(
        `/api/v1/spray/operator-sessions/${comboId}/mix-preview`,
        { headers: authHeaders() },
      );
      return res.data;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },
};
